import type { Modality } from '../contracts';
import type { BacktestRound } from './backtest';
import { sidesOf, type Side } from './custom';

/**
 * Descriptive reading of past rounds for the Análise page: balance between the two sides,
 * how it moved over the sample, runs (sequências) and, on Dígitos, each number's frequency.
 * Pure and synchronous; every figure is a count of what already happened.
 */

/**
 * Side a round closed on; null = Empate (closed level: Polarium returned every entry) or no reading.
 * On Dígitos too: an Empate is neither Par nor Ímpar, whatever digit it shows (owner 2026-10-23).
 */
export function roundSide(r: BacktestRound, modality: Modality): Side | null {
  if (modality === 'EVEN_ODD') return r.move === 'FLAT' || r.move === null ? null : r.digit % 2 === 0 ? 'EVEN' : 'ODD';
  return r.move === 'UP' ? 'RISE' : r.move === 'DOWN' ? 'FALL' : null;
}

/** Runs are grouped from this length up ("6+"). */
export const RUN_CAP = 6;

export interface RunLengthRow {
  /** 1…RUN_CAP; the last row counts runs of RUN_CAP or more. */
  length: number;
  a: number;
  b: number;
}

export interface AfterRunRow {
  /** Rounds in a row of the same side; the last row is RUN_CAP or more. */
  length: number;
  /** The next round repeated the side. */
  continued: number;
  /** The next round flipped to the other side. */
  broke: number;
}

export interface DigitRow {
  digit: number;
  count: number;
  /** Rounds since it last closed (0 = the newest round); null = not in the sample. */
  ago: number | null;
}

export interface WindowPeak {
  /** Index (in `rounds`) of the window's last round; it starts `window - 1` rounds before. */
  end: number;
  /** Rounds of that side inside the window. */
  count: number;
  /** Sided rounds inside the window (less than `window` only when it holds rounds without a move). */
  sided: number;
  share: number;
}

export interface RoundAnalysis {
  total: number;
  sides: readonly [Side, Side];
  a: number;
  b: number;
  /** Rounds that closed on neither side (Sobe e Desce without a move). */
  neutral: number;
  /** Share of side A among sided rounds, 0–1 (0.5 when there are none). */
  shareA: number;
  /** |deviation| beyond what chance explains in this many rounds (≈95%, normal approximation). */
  unusual: boolean;
  /** Share of side A over the last `window` rounds, at every round from the window's end on. */
  rolling: { window: number; points: { index: number; share: number }[] };
  /** The stretch of `window` rounds where each side had its biggest share (the most recent on a tie). */
  peaks: { a: WindowPeak | null; b: WindowPeak | null };
  current: { side: Side; length: number } | null;
  longest: { a: number; b: number };
  /** Finished runs by length (the one still running is not counted). */
  runs: RunLengthRow[];
  /** What came next after N in a row, over the whole sample. */
  after: AfterRunRow[];
  /** The same, apart for runs of each side (`a` = runs of side A). */
  afterBySide: { a: AfterRunRow[]; b: AfterRunRow[] };
  /** Dígitos only. */
  digits: DigitRow[] | null;
}

export function rollingWindow(total: number): number {
  return total >= 200 ? 30 : total >= 80 ? 20 : 10;
}

/** `rounds` oldest first. `window` = rounds per stretch for the trend and the peaks (default by sample size). */
export function analyzeRounds(rounds: readonly BacktestRound[], modality: Modality, opts: { window?: number } = {}): RoundAnalysis {
  const sides = sidesOf(modality);
  const [A] = sides;
  const list = rounds.map((r) => roundSide(r, modality));
  const n = list.length;

  let a = 0;
  let b = 0;
  for (const s of list) {
    if (s === null) continue;
    if (s === A) a++;
    else b++;
  }
  const sided = a + b;
  const shareA = sided ? a / sided : 0.5;
  const unusual = sided >= 20 && Math.abs(a - sided / 2) > 1.96 * Math.sqrt(sided / 4);

  // Rolling share: neutral rounds take a slot in the window but count for neither side.
  const window = Math.max(2, opts.window ?? rollingWindow(n));
  const points: { index: number; share: number }[] = [];
  let peakA: WindowPeak | null = null;
  let peakB: WindowPeak | null = null;
  let inA = 0;
  let inSided = 0;
  for (let i = 0; i < n; i++) {
    const s = list[i];
    if (s !== null && s !== undefined) {
      inSided++;
      if (s === A) inA++;
    }
    if (i >= window) {
      const out = list[i - window];
      if (out !== null && out !== undefined) {
        inSided--;
        if (out === A) inA--;
      }
    }
    if (i >= window - 1 && inSided) {
      const share = inA / inSided;
      points.push({ index: i, share });
      if (!peakA || share >= peakA.share) peakA = { end: i, count: inA, sided: inSided, share };
      if (!peakB || 1 - share >= peakB.share) peakB = { end: i, count: inSided - inA, sided: inSided, share: 1 - share };
    }
  }

  // Runs: an Empate is skipped — it neither counts nor ends the run (5 Par, Empate, 5 Par = 10 Par; owner
  // 2026-10-23). A missing round (hole in the data) still ends it.
  const runs: RunLengthRow[] = Array.from({ length: RUN_CAP }, (_, i) => ({ length: i + 1, a: 0, b: 0 }));
  const afterRows = (): AfterRunRow[] => Array.from({ length: RUN_CAP }, (_, i) => ({ length: i + 1, continued: 0, broke: 0 }));
  const after = afterRows();
  const afterBySide = { a: afterRows(), b: afterRows() };
  const longest = { a: 0, b: 0 };
  let runSide: Side | null = null;
  let runLen = 0;
  const closeRun = () => {
    if (!runSide || !runLen) return;
    const row = runs[Math.min(runLen, RUN_CAP) - 1]!;
    if (runSide === A) row.a++;
    else row.b++;
  };
  // A missing round (hole in the data: its id skipped) breaks the run: the rounds around it are not "in a row".
  const gapBefore = (i: number) => i > 0 && i < n && rounds[i]!.id !== rounds[i - 1]!.id + 1;
  for (let i = 0; i < n; i++) {
    const s = list[i]!;
    if (gapBefore(i)) {
      closeRun();
      runSide = null;
      runLen = 0;
    }
    if (s === null) continue;
    if (s === runSide) runLen++;
    else {
      closeRun();
      runSide = s;
      runLen = 1;
    }
    if (s === A) longest.a = Math.max(longest.a, runLen);
    else longest.b = Math.max(longest.b, runLen);
    // The next round with a side (Empates skipped), never across a hole.
    let j = i + 1;
    while (j < n && list[j] === null && !gapBefore(j)) j++;
    const next = list[j];
    if (next === undefined || next === null || gapBefore(j)) continue;
    const k = Math.min(runLen, RUN_CAP) - 1;
    for (const row of [after[k]!, (s === A ? afterBySide.a : afterBySide.b)[k]!]) {
      if (next === s) row.continued++;
      else row.broke++;
    }
  }
  const current = runSide && runLen ? { side: runSide, length: runLen } : null;

  let digits: DigitRow[] | null = null;
  if (modality === 'EVEN_ODD') {
    digits = Array.from({ length: 10 }, (_, digit) => ({ digit, count: 0, ago: null as number | null }));
    for (let i = 0; i < n; i++) {
      // An Empate shows no number (as on the game screen).
      if (list[i] === null) continue;
      const row = digits[rounds[i]!.digit]!;
      row.count++;
      row.ago = n - 1 - i;
    }
  }

  return { total: n, sides, a, b, neutral: n - sided, shareA, unusual, rolling: { window, points }, peaks: { a: peakA, b: peakB }, current, longest, runs, after, afterBySide, digits };
}

export interface PatternOutcome {
  /** Times the pattern closed with a round after it. */
  occurrences: number;
  /** What the round right after it closed on. */
  nextA: number;
  nextB: number;
  nextNeutral: number;
  /** Index (in `rounds`) of the last round of each match, oldest first; includes a match on the newest round. */
  ends: number[];
}

/** Every place the sides in `pattern` (oldest → newest) happened in a row, and what came right after. */
export function patternOutcome(rounds: readonly BacktestRound[], modality: Modality, pattern: readonly Side[]): PatternOutcome {
  const [A] = sidesOf(modality);
  const list = rounds.map((r) => roundSide(r, modality));
  const out: PatternOutcome = { occurrences: 0, nextA: 0, nextB: 0, nextNeutral: 0, ends: [] };
  const L = pattern.length;
  if (!L) return out;
  // Start of the stretch without missing rounds that each round belongs to: a match never spans a hole.
  const from: number[] = [];
  for (let i = 0; i < rounds.length; i++) from[i] = i > 0 && rounds[i]!.id === rounds[i - 1]!.id + 1 ? from[i - 1]! : i;
  for (let end = L - 1; end < list.length; end++) {
    if (from[end]! > end - L + 1) continue;
    let ok = true;
    for (let k = 0; k < L; k++) {
      if (list[end - L + 1 + k] !== pattern[k]) {
        ok = false;
        break;
      }
    }
    if (!ok) continue;
    out.ends.push(end);
    if (end + 1 >= list.length || from[end + 1] !== from[end]) continue;
    out.occurrences++;
    const next = list[end + 1];
    if (next === null) out.nextNeutral++;
    else if (next === A) out.nextA++;
    else out.nextB++;
  }
  return out;
}

export interface LevelEpisode {
  side: Side;
  /** Index (in `rounds`) of the first round of the first window at the level. */
  start: number;
  /** Index of the round whose window first reached the level (when it happened). */
  reachedAt: number;
  /** Index of the last round of the last window still at the level. */
  end: number;
  /** Biggest share of the side during the episode, 0–1. */
  best: number;
}

export interface LevelRow {
  /** Share of one side over the window, 0–1 (0.8 = "80/20"). */
  level: number;
  /** Episodes of side A / side B. */
  a: number;
  b: number;
  /** Both sides, oldest first. */
  episodes: LevelEpisode[];
}

/**
 * How many times one side reached `level` of the last `window` rounds ("chegou a 80/20?"), from the
 * analysis' rolling share. Consecutive windows at the level are one episode, so a long stretch
 * counts once.
 */
export function levelEpisodes(analysis: RoundAnalysis, levels: readonly number[]): LevelRow[] {
  const { points, window } = analysis.rolling;
  return levels.map((level) => {
    const episodes: LevelEpisode[] = [];
    for (const k of [0, 1] as const) {
      const side = analysis.sides[k];
      let open: LevelEpisode | null = null;
      let prev = -2;
      for (const p of points) {
        const share = k === 0 ? p.share : 1 - p.share;
        const ok = share >= level - 1e-9;
        if (open && (!ok || p.index !== prev + 1)) {
          episodes.push(open);
          open = null;
        }
        if (ok) {
          if (!open) open = { side, start: Math.max(0, p.index - window + 1), reachedAt: p.index, end: p.index, best: share };
          else {
            open.end = p.index;
            open.best = Math.max(open.best, share);
          }
        }
        prev = p.index;
      }
      if (open) episodes.push(open);
    }
    episodes.sort((x, y) => x.reachedAt - y.reachedAt);
    const a = episodes.filter((e) => e.side === analysis.sides[0]).length;
    return { level, a, b: episodes.length - a, episodes };
  });
}

export interface RunSpan {
  side: Side;
  length: number;
  /** Close time (epoch s) of the run's first and last rounds. */
  startAt: number;
  endAt: number;
}

/**
 * The longest run of each side in `rounds` (oldest first), with when it happened. An Empate is skipped
 * (the run goes on across it, like analyzeRounds); a missing round (id skipped) ends the run. On a tie the earliest run keeps the title:
 * a record is beaten only by a longer run. The run still going at the end counts as it is now.
 */
export function longestRunsBySide(rounds: readonly BacktestRound[], modality: Modality): { a: RunSpan | null; b: RunSpan | null } {
  const [A] = sidesOf(modality);
  const best: { a: RunSpan | null; b: RunSpan | null } = { a: null, b: null };
  let side: Side | null = null;
  let length = 0;
  let startAt = 0;
  let prevId: number | null = null;
  for (const r of rounds) {
    const s = roundSide(r, modality);
    // A missing round (its id skipped) breaks the run: a record is never made of two stretches glued together.
    if (prevId !== null && r.id !== prevId + 1) {
      side = null;
      length = 0;
    }
    prevId = r.id;
    if (s === null) continue;
    if (s === side) length++;
    else {
      side = s;
      length = 1;
      startAt = r.endAt;
    }
    const k = s === A ? 'a' : 'b';
    const cur = best[k];
    // Strictly longer: a tie never takes the title (a run growing past its own length does).
    if (!cur || length > cur.length) best[k] = { side: s, length, startAt, endAt: r.endAt };
  }
  return best;
}
